/**
 * Cart JS
 *    This script contains all cart related js functionality
 */

// Events
const EVT_CART_UPDATED = "cart:updated";
const EVT_CART_FINISH = "cart:finished";
const EVT_CART_PENDING = "cart:pending";

// Classes
const CLASS_HIDDEN = "is-hidden";
const CLASS_SCROLL_DISABLED = "is-scroll-disabled";
const CLASS_DISABLED = "is-disabled";

// Selectors
const SEL_CART_CONTAINER = "[data-cart-container]";
const SEL_CHECKOUT_BUTTON = "[data-checkout-button]";

const SEL_CART_DRAWER = "[data-cart-drawer]";
const SEL_CART_DRAWER_TOGGLE = "[data-cart-drawer-toggle]";

const SEL_QUANTITY_SELECTOR = "[data-quantity-selector]";

const SEL_PLUS = "[data-plus]";
const SEL_MINUS = "[data-minus]";
const SEL_INPUT = "[data-input]";
const SEL_ITEM_REMOVE = "[data-item-remove]";

const SEL_UPSELL_SLIDER = '[data-cart-uppsell-slider]';
const SEL_UPSELL_SLIDER_PREV = '[data-slider-upsell-prev]';
const SEL_UPSELL_SLIDER_NEXT = '[data-slider-upsell-next]';
const SEL_UPSELL_ADD = '[data-upsell-add]';
const SEL_UPSELL_CART = '[data-cart-uppsell]';

// ATTR
const ATTR_VARIANT_ID = "data-variant-id";

/**
 * Cart
 */

class Cart {
  constructor() {
    this.container = null;
    this.quantitySelectors = [];

    document.addEventListener(EVT_CART_PENDING, this.onCartPending.bind(this));
  }

  async fetchCartContents() {
    if (!this.container) return;
    fetch('/?sections=cart-contents')
      .then(response => response.json())
      .then(data => {
        const contents = data['cart-contents'];
        const temp = document.createElement('div');
        temp.innerHTML = contents;
        const content = temp.firstElementChild;
        const innerContent = content.innerHTML;

        this.container.innerHTML = innerContent;
        document.dispatchEvent(new Event(EVT_CART_FINISH));
      })
      .catch(err => console.error('Failed to update cart drawer:', err));
  }

  initQuantitySelector() {
    if (!this.container) return;
    const quantitySelectors = Array.from(this.container.querySelectorAll(SEL_QUANTITY_SELECTOR));

    quantitySelectors.forEach(element => {
      this.quantitySelectors.push(new QuantitySelector(element));
    })
  }

  initItemRemoveButton() {
    if (!this.container) return;
    const removeButtons = Array.from(this.container.querySelectorAll(SEL_ITEM_REMOVE));
    removeButtons.forEach(e => e.addEventListener("click", this.onRemoveItem.bind(this)));
  }

  disableCheckout() {
    const checkout = this.container.querySelector(SEL_CHECKOUT_BUTTON);
    if (checkout) checkout.classList.add(CLASS_DISABLED);
  }

  disableItemRemove() {
    if (!this.container) return;
    const removeButtons = Array.from(this.container.querySelectorAll(SEL_ITEM_REMOVE));
    removeButtons.forEach(e => e.disabled = true);
  }

  updateCart(variant_id, quantity) {
    if (!variant_id) return;
    document.dispatchEvent(new Event(EVT_CART_PENDING));

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        updates: {
          [variant_id]: quantity
        }
      })
    })
    .then(res => res.json())
    .then(cart => {
      document.dispatchEvent(new CustomEvent(EVT_CART_UPDATED, { detail: cart }));
    })
    .catch(err => console.error('Error updating cart:', err));
  }

  onRemoveItem(e) {
    const targetEl = e.currentTarget;
    const variantId = targetEl.getAttribute(ATTR_VARIANT_ID);
    
    if (!variantId) return;

    this.updateCart(variantId, 0);
  }

  onCartPending() {
    this.quantitySelectors.forEach(qty => qty.disableSelector());
    this.disableCheckout();
    this.disableItemRemove();
  }
}

/**
 * Cart Drawer
 */

class CartDrawer extends Cart {
  constructor() {
    super();
    this.container = document.querySelector(SEL_CART_DRAWER);
    this.toggles = Array.from(document.querySelectorAll(SEL_CART_DRAWER_TOGGLE));

    this.init();
  }

  async init() {
    document.addEventListener(EVT_CART_UPDATED, this.onCartUpdated.bind(this));
    document.addEventListener(EVT_CART_FINISH, this.onCartFinish.bind(this));
    this.toggles.forEach(e => e.addEventListener("click", this.toggle.bind(this)));
    await this.fetchCartContents();
  }

  async onSubmitNote() {
    const note = document.querySelector('[data-cart-notes]');

    if(!note) return;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        updates: {},
        note: note.value
      })
    })
    .then(res => res.json())
    .catch(err => console.error('Error updating cart:', err));
  }

  async getCartItems() {
    try {
      const response = await fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const cart = await response.json();
        return cart.items; // Returns the cart items
      } else {
        console.error('Failed to fetch cart items');
        return [];
      }
    } catch (err) {
      console.error('Error fetching cart items:', err);
      return [];
    }
  }

  onAddCart(e) {
    const targetEl = e.currentTarget;

    const variantId = targetEl.getAttribute(ATTR_VARIANT_ID);
    
    if (!variantId) return;
    document.dispatchEvent(new Event(EVT_CART_PENDING));

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1
          }
        ]
      })
    })
    .then(res => res.json())
    .then(cart => {
      document.dispatchEvent(new CustomEvent(EVT_CART_UPDATED, { detail: cart }));
    })
    .catch(err => console.error('Error updating cart:', err));
  }

  getShopifyRecommendations = async (items) => {
    if (!items.length) return;
    const item = items[0];
    let {product_id} = item;

    const url = `/recommendations/products.json?product_id=${product_id}`;
    const options = {
      headers: {'Content-Type': 'application/json'},
    };
    try {
      const response = await fetch(url, options);
      if (response.ok) {
        const recommendations = await response.json();
        const recommendationsProducts = recommendations.products.filter(
          (recommendation) => recommendation.variants.length < 2
        );
        return recommendationsProducts;
      } else {
        upsellsRecommendations = [];
      }
    } catch (err) {
      window.console.error(err);
    }
  };

  getUpsellsOrRecommendations = async () => {
    const items = await this.getCartItems();
    const itemIdsInCart = items.map((item) => item.product_id);
    const upsellCart = document.querySelector(SEL_UPSELL_CART);
    let results = [];
    let upsellHtml = '';
    let upsellProduct = '';

    const recommendations = await this.getShopifyRecommendations(items);
    results = [...recommendations];

    results = results.filter((result) => result && !itemIdsInCart.includes(result.id));
    if (results.length > 5) results.length = 5;

    results.map((product) => {
      let featImg = product.featured_image;
      let featImgCode = '';
      if (featImg != undefined) {
        featImg = featImg.replace('.jpg', '_236x.jpg').replace('.png', '_236x.png');
        featImgCode = `<img src="${featImg}" class="c-cart-contents__upsell-image" alt="${product.title}" />`
      }
      

      let featImg2 =  product.images[1];
      let featImg2Code = '';
      let hasHover = false;
      if (featImg2 != undefined) {
        featImg2 = featImg2.replace('.jpg', '_236x.jpg').replace('.png', '_236x.png');
        featImg2Code = `<img src="${featImg2}" class="c-cart-contents__upsell-image hover" alt="${product.title}" />`
        hasHover = true;
      }  
      upsellProduct += `
        <div class="slick-slide c-cart-contents__upsell-product">
          <div class="c-cart-contents__upsell-image-container hover-${hasHover}">
            <a href="${product.url}">
              ${featImgCode}
              ${featImg2Code}
            </a>
          </div>
          <div class="c-cart-contents__upsell-product-info">
            <div class="c-cart-contents__upsell-product-title-wrapper">
              <a href="${product.url}" class="c-cart-contents__upsell-product-title"><p class="c-cart-contents__upsell-product-title">${product.title}</p></a>
            </div>
            <p class="c-cart-contents__upsell-product-price">${theme.Currency.formatMoney(
              product.price,theme.moneyFormat)}</p>
              <button class="c-cart-contents__upsell-product-button addtocartinline add_to_cart" data-upsell-add data-variant-id="${product.variants[0].id}">
                Add <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16"><path stroke="#000" stroke-linecap="round" stroke-width="1.2" d="M3.333 8h9.334M8 3.333v9.334"/></svg>
              </button>
          </div>
        </div>
      `;
    }
    );

    upsellHtml = `<div><div data-cart-uppsell-slider>${upsellProduct}</div></div>`;
    upsellCart.innerHTML = upsellHtml;

    setTimeout(() => {
      document.querySelectorAll(SEL_UPSELL_ADD).forEach(button => button.addEventListener('click', this.onAddCart.bind(this)))
      const upsellCarouselElement = document.querySelector(SEL_UPSELL_SLIDER);
      document.querySelector('.c-cart-contents__upsell').classList.add('initialized');

      if (upsellCarouselElement) {
        $(upsellCarouselElement).slick({
          //slidesToShow: 3.5,
          variableWidth: true,   
          slidesToScroll: 1,
          centerMode: false,
          infinite: true, 
          arrows: false,
          responsive: [
            {
              breakpoint: 482,
              settings: {
        
              }                  
            }
          ]
        });

        $(SEL_UPSELL_SLIDER_PREV).click(function(){
          $(SEL_UPSELL_SLIDER).slick('slickPrev');
        });

        $(SEL_UPSELL_SLIDER_NEXT).click(function(){
          $(SEL_UPSELL_SLIDER).slick('slickNext');
        });
      } else {
        console.error('Upsell element not found');
      }
    }, 500);
    // return results;
  };

  attachToggleEvent() {
    const drawerToggle = this.container.querySelector(SEL_CART_DRAWER_TOGGLE);
    drawerToggle?.addEventListener("click", this.toggle.bind(this));

    const checkout = this.container.querySelector(SEL_CHECKOUT_BUTTON);

    // On Checkout Click
    checkout?.addEventListener('click', (e)=> {
      e.preventDefault();
      this.onSubmitNote();
      setTimeout(()=> { window.location.href = '/checkout'; }, 200);
    })
  }
  
  toggle(e) {
    e.preventDefault();
    this.container.classList.toggle(CLASS_HIDDEN);
    const isHidden = this.container.classList.contains(CLASS_HIDDEN);
    document.body.classList.toggle(CLASS_SCROLL_DISABLED, !isHidden);

    if(this.container.classList.contains(CLASS_HIDDEN)) this.onSubmitNote();
  }

  open() {
    this.container.classList.remove(CLASS_HIDDEN);
    document.body.classList.add(CLASS_SCROLL_DISABLED);
  }

  onCartFinish() {
    this.attachToggleEvent();
    this.initQuantitySelector();
    this.initItemRemoveButton();

    this.getUpsellsOrRecommendations();
  }

  async onCartUpdated() {
    await this.fetchCartContents();
    this.open();
  }
}

/**
 * Quantity Selector
 */

class QuantitySelector {
  constructor(container) {
    this.container = container;
    this.plusButton = container.querySelector(SEL_PLUS);
    this.minusButton = container.querySelector(SEL_MINUS);
    this.input = container.querySelector(SEL_INPUT);
    this.timeout = null;
    this.variantId = this.input?.getAttribute(ATTR_VARIANT_ID);
    
    this.attachEvents();
  }

  attachEvents() {
    this.plusButton.addEventListener("click", this.onPlusClick.bind(this));
    this.minusButton.addEventListener("click", this.onMinusClick.bind(this));
    this.input.addEventListener("input", this.onInputChange.bind(this));
  }

  get minQuantity() {
    return this.input ? parseInt(this.input.getAttribute("min")) : 1;
  }

  get quantity() {
    return this.input ? parseInt(this.input.value) : 1;
  }

  set quantity(value) {
    this.input.value = value;
  }

  disableSelector() {
    this.plusButton.disabled = true;
    this.minusButton.disabled = true;
    this.input.disabled = true;
  }

  updateCart() {
    if (!this.variantId) return;
    if (this.timeout) clearTimeout(this.timeout);
    
    this.timeout = setTimeout(() => {
      document.dispatchEvent(new Event(EVT_CART_PENDING));

      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          updates: {
            [this.variantId]: this.quantity
          }
        })
      })
      .then(res => res.json())
      .then(cart => {
        document.dispatchEvent(new CustomEvent(EVT_CART_UPDATED, { detail: cart }));
      })
      .catch(err => console.error('Error updating cart:', err));
    }, 500);
  }
  
  // Events

  onPlusClick() {
    this.quantity = this.quantity + 1;
    this.updateCart();
  }

  onMinusClick() {
    const newQuantity = this.quantity > 1 ? this.quantity - 1 : 1;
    if (newQuantity == this.quantity) return;
    this.quantity = newQuantity;
    this.updateCart();
  }

  onInputChange() {
    this.updateCart();
  }
}

// Initialize cart
document.addEventListener('DOMContentLoaded', function () {
  new CartDrawer;
});